import { useEffect, useState } from "react";
import {
  Pressable,
  ActivityIndicator,
  Modal,
  View,
  Text,
} from "react-native";
import styled from "styled-components/native";
import { useLocalSearchParams } from "expo-router";
import { MemberListSkeleton } from "@/components/Skeleton";
import {
  collection,
  query,
  where,
  onSnapshot,
  doc,
  setDoc,
  getDoc,
  Timestamp,
} from "firebase/firestore";
import { db } from "@/lib/firebase";
import { useAuth } from "@/contexts/AuthContext";
import { useClub } from "@/contexts/ClubContext";
import { useMembers } from "@/hooks/useMembers";
import { useTeamConfig } from "@/hooks/useTeamConfig";
import { colors, radius, font } from "@/lib/theme";
import type { ChannelMember } from "@/lib/types";
import ContentWidth, { ModalContent } from "@/components/ContentWidth";
import { useAlert } from "@/hooks/useAlert";
import AlertSheet from "@/components/AlertSheet";

/* ── Styled Components ── */

const Screen = styled.ScrollView`
  flex: 1;
  background-color: ${colors.bg};
`;

const Center = styled.View`
  flex: 1;
  background-color: ${colors.bg};
  justify-content: center;
  align-items: center;
`;

const Header = styled.View`
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding-horizontal: 20px;
  padding-top: 16px;
  padding-bottom: 8px;
`;

const HeaderLabel = styled.Text`
  font-size: 12px;
  font-weight: ${font.extrabold};
  color: ${colors.inkMuted};
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const InviteBtnWrap = styled.View`
  background-color: ${colors.brand};
  border-radius: ${radius.md}px;
  padding-horizontal: 16px;
  padding-vertical: 10px;
`;

const InviteBtnText = styled.Text`
  color: #fff;
  font-size: 12px;
  font-weight: ${font.bold};
`;

const SectionHeader = styled.View`
  flex-direction: row;
  align-items: center;
  gap: 8px;
  padding-horizontal: 20px;
  padding-top: 20px;
  padding-bottom: 8px;
`;

const SectionLabel = styled.Text`
  font-size: 12px;
  font-weight: ${font.extrabold};
  color: ${colors.inkMuted};
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const Card = styled.View`
  margin-horizontal: 20px;
  background-color: ${colors.white};
  border-radius: ${radius.lg}px;
  border-width: 1px;
  border-color: ${colors.line};
  overflow: hidden;
`;

const EmptyCard = styled.View`
  margin-horizontal: 20px;
  margin-top: 20px;
  background-color: ${colors.white};
  border-radius: ${radius.lg}px;
  border-width: 1px;
  border-color: ${colors.line};
  padding: 32px;
  align-items: center;
`;

const EmptyText = styled.Text`
  font-size: 14px;
  color: ${colors.inkMuted};
  text-align: center;
`;

const Row = styled.View<{ bordered?: boolean }>`
  flex-direction: row;
  align-items: center;
  padding-horizontal: 16px;
  padding-vertical: 14px;
  ${(p) =>
    p.bordered
      ? `border-bottom-width: 1px; border-bottom-color: ${colors.line};`
      : ""}
`;

const Avatar = styled.View`
  width: 40px;
  height: 40px;
  border-radius: 20px;
  background-color: ${colors.brandLight};
  align-items: center;
  justify-content: center;
  margin-right: 12px;
`;

const AvatarText = styled.Text`
  font-size: 14px;
  font-weight: ${font.extrabold};
  color: ${colors.brandDark};
`;

const Flex1 = styled.View`
  flex: 1;
`;

const MemberName = styled.Text`
  font-size: 14px;
  font-weight: ${font.bold};
  color: ${colors.ink};
`;

const MemberSub = styled.Text`
  font-size: 12px;
  color: ${colors.inkMuted};
  font-weight: ${font.medium};
  margin-top: 2px;
`;

const RoleBadge = styled.View<{ bg: string; borderClr: string }>`
  border-radius: ${radius.full}px;
  padding-horizontal: 8px;
  padding-vertical: 3px;
  border-width: 1px;
  background-color: ${(p) => p.bg};
  border-color: ${(p) => p.borderClr};
`;

const RoleBadgeText = styled.Text<{ clr: string }>`
  font-size: 11px;
  font-weight: ${font.bold};
  color: ${(p) => p.clr};
`;

const ModalWrap = styled.View`
  background-color: ${colors.white};
  border-top-left-radius: ${radius.xl}px;
  border-top-right-radius: ${radius.xl}px;
  padding: 24px;
  padding-bottom: 40px;
  max-height: 70%;
`;

const ModalTitle = styled.Text`
  font-size: 20px;
  font-weight: ${font.extrabold};
  color: ${colors.ink};
  margin-bottom: 4px;
`;

const ModalDesc = styled.Text`
  font-size: 14px;
  color: ${colors.inkSecondary};
  line-height: 20px;
  margin-bottom: 16px;
`;

const ModalScroll = styled.ScrollView``;

const AddBtnWrap = styled.View`
  background-color: ${colors.brand};
  border-radius: ${radius.sm}px;
  padding-horizontal: 12px;
  padding-vertical: 6px;
`;

const AddBtnText = styled.Text`
  color: #fff;
  font-size: 12px;
  font-weight: ${font.bold};
`;

const CancelBtnWrap = styled.View`
  padding-vertical: 12px;
  align-items: center;
  margin-top: 8px;
`;

const CancelBtnText = styled.Text`
  color: ${colors.inkMuted};
  font-size: 15px;
  font-weight: ${font.bold};
`;

const Spacer = styled.View`
  height: 32px;
`;

/* ── Screen ── */

export default function ChannelMembersScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const { user } = useAuth();
  const { club, membership } = useClub();
  const { members: clubMembers, loading: clubMembersLoading } = useMembers(
    club?.id
  );
  const { isAnyCaptain } = useTeamConfig(club?.id);
  const { alertProps, alert } = useAlert();

  const [channelMembers, setChannelMembers] = useState<
    (ChannelMember & { id: string })[]
  >([]);
  const [pendingMembers, setPendingMembers] = useState<
    (ChannelMember & { id: string })[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [inviting, setInviting] = useState<string | null>(null);

  const canManage =
    membership?.role === "admin" || isAnyCaptain(user?.uid);

  // Listen to channel members
  useEffect(() => {
    if (!club?.id || !id) {
      setChannelMembers([]);
      setLoading(false);
      return;
    }

    const q = query(
      collection(db, "clubs", club.id, "channels", id, "members"),
      where("status", "==", "active")
    );

    const unsub = onSnapshot(q, (snap) => {
      setChannelMembers(
        snap.docs.map((d) => ({ id: d.id, ...d.data() })) as any
      );
      setLoading(false);
    });

    // Pending requests (admins act on these)
    const pendingQuery = query(
      collection(db, "clubs", club.id, "channels", id, "members"),
      where("status", "==", "pending")
    );
    const unsubPending = onSnapshot(pendingQuery, (snap) => {
      setPendingMembers(snap.docs.map((d) => ({ id: d.id, ...d.data() })) as any);
    });

    return () => {
      unsub();
      unsubPending();
    };
  }, [club?.id, id]);

  const handleApprove = async (userId: string) => {
    if (!club?.id || !id) return;
    try {
      await setDoc(
        doc(db, "clubs", club.id, "channels", id, "members", userId),
        { status: "active", joinedAt: Timestamp.now() },
        { merge: true }
      );
    } catch {}
  };

  const handleReject = async (userId: string) => {
    if (!club?.id || !id) return;
    try {
      const { deleteDoc } = await import("firebase/firestore");
      await deleteDoc(doc(db, "clubs", club.id, "channels", id, "members", userId));
    } catch {}
  };

  // Club members who are NOT already in the channel (active or pending)
  const [allChannelMemberIds, setAllChannelMemberIds] = useState<Set<string>>(
    new Set()
  );

  useEffect(() => {
    if (!club?.id || !id) return;

    const unsub = onSnapshot(
      collection(db, "clubs", club.id, "channels", id, "members"),
      (snap) => {
        const ids = new Set(snap.docs.map((d) => d.id));
        setAllChannelMemberIds(ids);
      }
    );

    return unsub;
  }, [club?.id, id]);

  const invitableMembers = clubMembers.filter(
    (m) => !allChannelMemberIds.has(m.userId)
  );

  const handleInvite = async (
    userId: string,
    displayName: string,
    email: string
  ) => {
    if (!club?.id || !id) return;
    setInviting(userId);
    try {
      await setDoc(
        doc(db, "clubs", club.id, "channels", id, "members", userId),
        {
          userId,
          displayName,
          email,
          role: "member",
          status: "active",
          joinedAt: Timestamp.now(),
        }
      );
    } catch (err: any) {
      const msg = err.message || "Failed to invite member.";
      await alert({ title: "Error", emoji: "😕", message: msg, buttonColor: "#dc2626" });
    } finally {
      setInviting(null);
    }
  };

  if (loading || clubMembersLoading) {
    return (
      <View style={{ flex: 1, backgroundColor: colors.bg }}>
        <MemberListSkeleton />
      </View>
    );
  }

  return (
    <Screen showsVerticalScrollIndicator={false}>
      <ContentWidth>
        {/* Header */}
        <Header>
          <HeaderLabel>{channelMembers.length} members</HeaderLabel>
          {canManage && (
            <Pressable
              style={({ pressed }) => (pressed ? { opacity: 0.9 } : {})}
              onPress={() => setShowInvite(true)}
            >
              <InviteBtnWrap>
                <InviteBtnText>+ Invite</InviteBtnText>
              </InviteBtnWrap>
            </Pressable>
          )}
        </Header>

        {/* Pending requests */}
        {canManage && pendingMembers.length > 0 && (
          <>
            <SectionHeader>
              <SectionLabel>Pending requests ({pendingMembers.length})</SectionLabel>
            </SectionHeader>
            <Card>
              {pendingMembers.map((m, i) => (
                <Row key={m.id} bordered={i < pendingMembers.length - 1}>
                  <Avatar>
                    <AvatarText>{m.displayName?.[0]?.toUpperCase() || "?"}</AvatarText>
                  </Avatar>
                  <Flex1>
                    <MemberName>{m.displayName}</MemberName>
                  </Flex1>
                  <View style={{ flexDirection: "row", gap: 8 }}>
                    <Pressable onPress={() => handleApprove(m.userId)}>
                      <View style={{ backgroundColor: colors.brand, paddingHorizontal: 12, paddingVertical: 6, borderRadius: radius.sm }}>
                        <Text style={{ color: "#fff", fontSize: 12, fontWeight: "700" }}>Approve</Text>
                      </View>
                    </Pressable>
                    <Pressable onPress={() => handleReject(m.userId)}>
                      <View style={{ backgroundColor: colors.bg, borderWidth: 1, borderColor: colors.line, paddingHorizontal: 12, paddingVertical: 6, borderRadius: radius.sm }}>
                        <Text style={{ color: colors.inkMuted, fontSize: 12, fontWeight: "700" }}>Reject</Text>
                      </View>
                    </Pressable>
                  </View>
                </Row>
              ))}
            </Card>
          </>
        )}

        {/* Active members */}
        <SectionHeader>
          <SectionLabel>Active</SectionLabel>
        </SectionHeader>

        {channelMembers.length > 0 ? (
          <Card>
            {channelMembers.map((m, i) => {
              const roleStyle =
                m.role === "admin"
                  ? {
                      bg: colors.brand50,
                      text: colors.brandDark,
                      border: colors.brandLight,
                    }
                  : {
                      bg: colors.bg,
                      text: colors.inkMuted,
                      border: colors.line,
                    };
              return (
                <Row key={m.id} bordered={i < channelMembers.length - 1}>
                  <Avatar>
                    <AvatarText>
                      {m.displayName?.[0]?.toUpperCase() || "?"}
                    </AvatarText>
                  </Avatar>
                  <Flex1>
                    <MemberName>{m.displayName}</MemberName>
                  </Flex1>
                  <RoleBadge bg={roleStyle.bg} borderClr={roleStyle.border}>
                    <RoleBadgeText clr={roleStyle.text}>
                      {m.role}
                    </RoleBadgeText>
                  </RoleBadge>
                </Row>
              );
            })}
          </Card>
        ) : (
          <EmptyCard>
            <EmptyText>No active members in this channel.</EmptyText>
          </EmptyCard>
        )}

        <Spacer />
      </ContentWidth>

      {/* Invite modal */}
      <Modal visible={showInvite} animationType="slide" transparent>
        <ModalContent
          onClose={() => setShowInvite(false)}
        >
          <ModalWrap>
            <ModalTitle>Invite to Channel</ModalTitle>
            <ModalDesc>
              Select a club member to add directly to this channel.
            </ModalDesc>

            <ModalScroll showsVerticalScrollIndicator={false}>
              {invitableMembers.length > 0 ? (
                <Card style={{ marginHorizontal: 0 }}>
                  {invitableMembers.map((m, i) => (
                    <Row
                      key={m.id}
                      bordered={i < invitableMembers.length - 1}
                    >
                      <Avatar>
                        <AvatarText>
                          {m.displayName?.[0]?.toUpperCase() || "?"}
                        </AvatarText>
                      </Avatar>
                      <Flex1>
                        <MemberName>{m.displayName}</MemberName>
                        <MemberSub>{m.email}</MemberSub>
                      </Flex1>
                      <Pressable
                        style={({ pressed }) =>
                          pressed ? { opacity: 0.7 } : {}
                        }
                        onPress={() =>
                          handleInvite(m.userId, m.displayName, m.email)
                        }
                        disabled={inviting === m.userId}
                      >
                        <AddBtnWrap>
                          {inviting === m.userId ? (
                            <ActivityIndicator size="small" color="#fff" />
                          ) : (
                            <AddBtnText>Add</AddBtnText>
                          )}
                        </AddBtnWrap>
                      </Pressable>
                    </Row>
                  ))}
                </Card>
              ) : (
                <EmptyCard style={{ marginHorizontal: 0 }}>
                  <EmptyText>
                    All club members are already in this channel.
                  </EmptyText>
                </EmptyCard>
              )}
            </ModalScroll>

            <Pressable
              style={({ pressed }) => (pressed ? { opacity: 0.7 } : {})}
              onPress={() => setShowInvite(false)}
            >
              <CancelBtnWrap>
                <CancelBtnText>Close</CancelBtnText>
              </CancelBtnWrap>
            </Pressable>
          </ModalWrap>
        </ModalContent>
      </Modal>
      <AlertSheet {...alertProps} />
    </Screen>
  );
}
